<!--
  SupportAttachmentPicker.svelte — picks the files attached to a ticket or reply.

  A dashed drop zone rather than the browser's own file input: the native
  control cannot be themed (its button is white in light mode and its text is
  cropped against the depth surfaces in dark mode), and a drop zone is what
  people dragging a crash dump or scene file expect. The zone is a real
  `<label>` for a visually hidden input, so a click, Enter or Space opens the
  picker and assistive technology announces it as a file control.

  Files dropped or picked twice are kept once (same name and size); the list
  below the zone removes them one at a time, and shows a thumbnail for an image.
  The parent owns the list.
-->
<script lang="ts">
	import SupportFileThumbnail from './SupportFileThumbnail.svelte';
	import { formatFileSize, isPreviewableImage, mergeAttachments } from '../support/supportFormat';
	import { t, t2 } from '../i18n';

	interface Props {
		/** The files picked so far. Bound: the parent sends them. */
		files?: File[];
		/** Whether picking is disabled. */
		disabled?: boolean;
		/** Id of the hidden file input, for an external `<label for>`. */
		id?: string;
		/** Test id on the hidden input. */
		testid?: string;
		/** A one-line zone, for tight spaces such as the reply composer. */
		compact?: boolean;
	}

	let { files = $bindable([]), disabled = false, id = 'support-files', testid, compact = false }: Props = $props();

	/** Whether a drag is over the zone, for the highlight. */
	let dragging = $state(false);

	/** Adds picked files, skipping duplicates. */
	function addFiles(event: Event): void {
		const input = event.currentTarget as HTMLInputElement;
		files = mergeAttachments(files, Array.from(input.files ?? []));
		/* Cleared so picking the same file again after removing it fires `change` again. */
		input.value = '';
	}

	/** Takes dropped files. */
	function drop(event: DragEvent): void {
		event.preventDefault();
		dragging = false;
		/* A disabled zone still has to swallow the drop, or the browser opens the file. */
		if (disabled) return;
		files = mergeAttachments(files, Array.from(event.dataTransfer?.files ?? []));
	}

	/** Marks the zone as a drop target while a drag is over it. */
	function dragOver(event: DragEvent): void {
		event.preventDefault();
		dragging = !disabled;
	}

	/** Removes one file. */
	function removeFile(index: number): void {
		files = files.filter((_, i) => i !== index);
	}
</script>

<div>
	<label
		for={id}
		class="bg-depth flex items-center justify-center gap-2 rounded-xl border-2 border-dashed text-sm transition
			{compact ? 'px-3 py-2' : 'flex-col px-4 py-6 text-center'}
			{dragging
			? 'border-primary-500 text-primary-600 dark:text-primary-400'
			: 'text-default border-gray-300 dark:border-gray-600'}
			{disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-primary-400 cursor-pointer'}"
		ondragover={dragOver}
		ondragleave={() => (dragging = false)}
		ondrop={drop}
		data-testid="attachment-zone"
	>
		<i class="fa-solid {compact ? 'fa-paperclip' : 'fa-cloud-arrow-up text-2xl'}" aria-hidden="true"></i>
		<span>
			<span class="text-primary-600 dark:text-primary-400 font-medium">{$t('Attach files')}</span>
			{$t('or drop them here')}
		</span>
		<input {id} type="file" multiple class="sr-only" onchange={addFiles} {disabled} data-testid={testid} />
	</label>

	{#if files.length > 0}
		<ul class="mt-2 flex flex-wrap gap-2" aria-label={$t('Files to attach')}>
			{#each files as file, index (file.name + file.size)}
				<li class="bg-depth inline-flex items-center gap-2 rounded-lg px-2 py-1 text-xs">
					{#if isPreviewableImage(file.type)}
						<SupportFileThumbnail {file} />
					{:else}
						<i class="fa-solid fa-paperclip"></i>
					{/if}
					<span class="font-medium break-all">{file.name}</span>
					<span class="text-default">{formatFileSize(file.size)}</span>
					<button
						type="button"
						class="text-gray-500 hover:text-red-600"
						onclick={() => removeFile(index)}
						aria-label={t2('Remove {{name}}', { name: file.name })}
					>
						<i class="fa-solid fa-xmark"></i>
					</button>
				</li>
			{/each}
		</ul>
	{/if}
</div>
